<template>
  <span v-if="!canEdit" :class="[isLightTheme && 'has-text-white']">
    {{
      $_.compact([
        originalValue !== 0 ? prefix : "",
        originalValue === 0
          ? $t("_.free")
          : valueFormatted !== null
          ? valueFormatted
          : value,
        originalValue !== 0 ? suffix : ""
      ]).join(" ")
    }}
  </span>
  <b-dropdown
    v-else
    ref="edit-price-popover"
    aria-role="list"
    :position="position"
    :mobile-modal="false"
    @active-change="editPriceDropdownToggled"
  >
    <template #trigger>
      <slot name="trigger">
        <a
          :title="
            $t('_action.edit_object', {
              object: $t('_.price').toString().toLowerCase()
            })
          "
          class="has-text-dark"
          :class="{
            'has-text-grey-light is-loading has-cursor-wait ': isLoading
          }"
          @click.stop.prevent="isLoading ? null : togglePopover()"
        >
          <del
            v-if="isPriceEdited"
            class="is-size-7 has-text-grey-light has-margin-right-25"
          >
            <em>{{
              `${originalValue !== 0 ? prefix : ""} ${
                originalValue === 0 ? $t("_.free") : originalValueFormatted
              }`
            }}</em>
          </del>
          <u>{{
            `${
              (!isPriceEdited && originalValue === 0) || value === 0
                ? ""
                : prefix
            } ${
              (!isPriceEdited && originalValue === 0) || value === 0
                ? $t("_.free")
                : valueFormatted !== null
                ? valueFormatted
                : value
            } ${
              (!isPriceEdited && originalValue === 0) || value === 0
                ? ""
                : suffix
            }`.trim()
          }}</u>
          <b-icon
            v-if="showIcon"
            :icon="isLoading ? 'spinner' : 'pen'"
            pack="fas"
            :class="[
              'is-size-7',
              'has-margin-left-25',
              isLoading && 'has-text-grey-light fa-pulse'
            ]"
            size="is-small"
          />
        </a>
      </slot>
    </template>
    <b-dropdown-item
      aria-role="listitem"
      custom
      :focusable="false"
      :style="{ width: '16rem' }"
    >
      <ValidationObserver ref="observer" slim>
        <form>
          <p class="has-margin-bottom-75 has-text-left is-size-8 has-text-grey">
            <span class="has-white-space-prewrap">{{ instruction }}</span>
          </p>
          <u-field
            v-if="isActive"
            :name="`${$t('_.price')}`.toLowerCase()"
            :vname="inputId"
            :show-error-message="false"
            :vid="inputId"
            rules="required|min_value:0"
            slim
          >
            <currency-input
              v-model.number="currentValue"
              :currency="currency"
              :show-code="true"
              :disabled="isDisabled"
              :min="minValue"
              :max="maxValue"
              :has-button-right="true"
              :input-id="inputId"
              :autofocus="true"
              class="has-flex-grow-1"
              @keypress.enter.native.prevent.stop.exact="emitInput"
            />
          </u-field>
          <a
            class="button is-success is-stretched has-margin-top-50"
            :disabled="hasErrors || isDisabled"
            @click="emitInput"
          >
            {{ $t("_action.apply") }}
          </a>
          <p v-if="showResetDefault" class="has-margin-top-50">
            <u-link class="has-text-grey" @click="resetDefault">{{
              $t("_.restore_default_price")
            }}</u-link>
          </p>
        </form>
      </ValidationObserver>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import HasValidation from "@/mixins/HasValidation";
import type { PropType } from "vue";
import type { ICurrency } from "@/models/constants";

export default defineComponent({
  name: "EditablePrice",
  mixins: [HasValidation],
  props: {
    canEdit: { type: Boolean, default: true },
    instruction: { type: String, default: "" },
    value: { type: Number },
    minValue: { type: Number, default: 0.0 },
    maxValue: { type: Number, default: null },
    originalValue: { type: Number, default: null },
    prefix: { type: String, default: "" },
    suffix: { type: String, default: "" },
    valueFormatted: { type: String, default: null },
    isLoading: { type: Boolean, default: false },
    inputId: { type: String, default: "price" },
    showIcon: { type: Boolean, default: true },
    originalValueFormatted: { type: String, default: null },
    currency: { type: Object as PropType<ICurrency>, required: true },
    isDisabled: { type: Boolean, default: false },
    position: { type: String, default: "is-bottom-left" },
    isLightTheme: { type: Boolean, default: false }
  },
  data: () => ({
    currentValue: 0,
    isActive: false
  }),
  computed: {
    isPriceEdited() {
      return (
        this.originalValueFormatted &&
        this.valueFormatted !== this.originalValueFormatted
      );
    },
    showResetDefault() {
      return (
        this.originalValue !== null &&
        this.value >= 0 &&
        this.value !== this.originalValue
      );
    }
  },
  methods: {
    editPriceDropdownToggled(active: boolean) {
      this.isActive = active;
      if (!active) return;
      this.currentValue = this.value;
    },
    resetDefault() {
      this.$emit("input", this.originalValue);
      this.togglePopover();
    },
    emitInput() {
      if (
        this.currentValue < 0 ||
        this.currentValue === null ||
        this.hasErrors ||
        this.isDisabled
      ) {
        return;
      }
      this.$emit("input", this.currentValue);
      this.togglePopover();
    },
    togglePopover() {
      this.$refs["edit-price-popover"].toggle();
    }
  }
});
</script>
